import {
  Fragment,
  type FunctionComponent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
// eslint-disable-next-line @retailer-platform/no-restricted-imports
import { useOs } from '@mantine/hooks'
import {
  MenuBase,
  MenuHeader,
  MenuItem,
  useTheme,
} from '@retailer-platform/shared-components/src/tds'
import { useDebouncedState } from '@retailer-platform/shared-components/src/hooks/useDebouncedState.hooks'
import { useTrackEvent } from '../../../utils/events/useTrackEvent.hook'
import {
  filterGroupsBySearch,
  formCategoriesIntoItemGroupsV3,
  getNavigateToSearchResultDataV3,
  getTrackEventNothingFoundDataV3,
  getTrackEventUserSearchedDataV3,
} from './searchV3.utils'
import { type SearchItemV3 } from './searchV3.types'
import { useV3SearchActions } from './useV3SearchActions'

type Props = {
  isOpen: boolean
  onChangeOpen: (next: boolean) => void
  // The TopBar trigger button. Owned by TdsSearchButton so it stays in sync
  // with the open/close, hotkey, and access-control logic in the shell.
  trigger: ReactNode
}

const TRACK_DEBOUNCE_MS = 2500

export const TdsNavSearchMenu: FunctionComponent<Props> = ({ isOpen, onChangeOpen, trigger }) => {
  const intl = useIntl()
  const os = useOs()
  const theme = useTheme()
  const trackEvent = useTrackEvent()
  const actions = useV3SearchActions()

  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, , setDebouncedTrigger, isDebouncePending] = useDebouncedState(
    '',
    TRACK_DEBOUNCE_MS
  )
  const [activeItemIndex, setActiveItemIndex] = useState(-1)

  const placeholder =
    os === 'macos' || os === 'ios'
      ? intl.formatMessage({ id: 'navV3.search.placeHolderMac' })
      : intl.formatMessage({ id: 'navV3.search.placeHolderWindows' })

  const resetSearch = useCallback(() => {
    setSearchValue('')
    setDebouncedTrigger('', true)
    setActiveItemIndex(-1)
  }, [setDebouncedTrigger])

  const groups = useMemo(
    () => formCategoriesIntoItemGroupsV3(actions, resetSearch),
    [actions, resetSearch]
  )
  const filteredGroups = useMemo(
    () => filterGroupsBySearch(groups, searchValue),
    [groups, searchValue]
  )
  const totalItemCount = useMemo(
    () => filteredGroups.reduce((acc, g) => acc + g.items.length, 0),
    [filteredGroups]
  )

  const activeItem = useMemo<SearchItemV3 | undefined>(() => {
    if (activeItemIndex < 0) return undefined
    let i = 0
    for (const group of filteredGroups) {
      for (const item of group.items) {
        if (i === activeItemIndex) return item
        i++
      }
    }
    return undefined
  }, [filteredGroups, activeItemIndex])

  const onSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value
      setSearchValue(next)
      setDebouncedTrigger(next)
      setActiveItemIndex(next === '' ? -1 : 0)
    },
    [setDebouncedTrigger]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'Enter': {
          if (activeItemIndex !== -1 && activeItem) {
            trackEvent(
              getNavigateToSearchResultDataV3(
                searchValue,
                activeItem.label,
                activeItem.route,
                'enter keyboard'
              )
            )
            onChangeOpen(false)
            // onClick already calls resetSearch as part of the wrapper added
            // by formCategoriesIntoItemGroupsV3.
            activeItem.onClick()
          }
          break
        }
        case 'ArrowDown': {
          if (totalItemCount === 0) break
          setActiveItemIndex(prev => (prev < totalItemCount - 1 ? prev + 1 : 0))
          event.stopPropagation()
          event.preventDefault()
          break
        }
        case 'ArrowUp': {
          if (totalItemCount === 0) break
          setActiveItemIndex(prev => (prev > 0 ? prev - 1 : totalItemCount - 1))
          event.stopPropagation()
          event.preventDefault()
          break
        }
        case 'Escape': {
          onChangeOpen(false)
          break
        }
      }
    },
    [activeItem, activeItemIndex, totalItemCount, searchValue, trackEvent, onChangeOpen]
  )

  // Tracking: fire `userSearched` (when results exist) or `nothingFound`
  // (when no results) once the user pauses typing. The debounce fires the
  // tracking call ~2.5s after the last keystroke.
  useEffect(() => {
    if (isDebouncePending || !debouncedSearchValue) return
    if (totalItemCount === 0) {
      trackEvent(getTrackEventNothingFoundDataV3(debouncedSearchValue))
    } else {
      trackEvent(getTrackEventUserSearchedDataV3(debouncedSearchValue))
    }
  }, [debouncedSearchValue, isDebouncePending, totalItemCount, trackEvent])

  // Reset when the menu closes so reopening starts clean.
  useEffect(() => {
    if (!isOpen) {
      setSearchValue('')
      setDebouncedTrigger('', true)
      setActiveItemIndex(-1)
    }
  }, [isOpen, setDebouncedTrigger])

  const onItemClick = useCallback(
    (item: SearchItemV3) => () => {
      trackEvent(getNavigateToSearchResultDataV3(searchValue, item.label, item.route, 'click'))
      onChangeOpen(false)
      item.onClick()
    },
    [searchValue, trackEvent, onChangeOpen]
  )

  const searchBarProps = {
    displaySearchBar: true,
    searchPlaceholderText: placeholder,
    onChange: onSearchChange,
    searchValue,
    onKeyDown: handleKeyDown,
    isAutoFocus: true,
  }

  // Flatten filtered groups to compute a per-item global index (matches
  // activeItemIndex). Done as a single pass while rendering.
  let cursor = 0

  return (
    <MenuBase
      target={trigger}
      isOpen={isOpen}
      onMenuStateChange={onChangeOpen}
      position="bottomEnd"
      searchBarProps={searchBarProps}
    >
      <div css={{ maxHeight: 400, width: 364, overflowY: 'auto' }}>
        {filteredGroups.length > 0 ? (
          filteredGroups.map(group => (
            <Fragment key={group.groupHeaderText}>
              <MenuHeader title={group.groupHeaderText} />
              {group.items.map(item => {
                const itemIndex = cursor++
                const isHover = itemIndex === activeItemIndex
                return (
                  <MenuItem
                    key={`${group.groupHeaderText}::${item.label}::${item.route}`}
                    text={item.label}
                    onClick={onItemClick(item)}
                    isHover={isHover}
                  />
                )
              })}
            </Fragment>
          ))
        ) : (
          <div css={{ padding: 24, color: theme.colors.systemGrayscale60 }}>
            {intl.formatMessage({ id: 'navV3.search.nothingFound' })}
          </div>
        )}
      </div>
    </MenuBase>
  )
}
